import React from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import CreateBlockTimeModal, {
  BLOCK_TIME_SCHEDULE_FACILITIES,
  BLOCK_TIME_SCROLL_SELECT_DATE,
  submitBlockTimePayloads,
} from "./CreateBlockTimeModal";
import CreateFacilityModal from "./CreateFacilityModal";
import SchedulingEmptyState from "./SchedulingEmptyState";
import { CardListSkeleton } from "./SchedulingSkeletons";
import { fetchSchedulingBookable } from "./scheduling-bookable-fetch";
import {
  consumeSchedulingCreateIntent,
  consumeSchedulingEditIntent,
} from "./scheduling-create-from-overview";
import {
  buildDuplicateFacilityPayload,
  resolveFacilityWindowsForDuplicate,
} from "./scheduling-duplicate-payloads";
import { canManageSchedulingRecord } from "./scheduling-event-ownership";
import { buildSchedulingCalendarPreviewUrl } from "./scheduling-preview-url";

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_INDEX_TO_KEY = WEEKDAY_KEYS;
const WEEKDAY_LABELS = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function normalizeAvailabilityToWeeklySlots(payload) {
  if (!payload || typeof payload !== "object") return null;

  const wa = payload.weekly_availability ?? payload.weeklyAvailability;
  if (wa && typeof wa === "object") {
    const out = {};
    for (const key of WEEKDAY_KEYS) {
      const capKey = key.charAt(0).toUpperCase() + key.slice(1);
      const raw = wa[key] ?? wa[capKey] ?? wa[key.toUpperCase()];
      if (raw == null) continue;
      if (Array.isArray(raw)) {
        out[key] = raw.map((x) =>
          typeof x === "object" && x !== null
            ? formatWindowSlot(x)
            : formatTimeRangeStringToAmPm(String(x))
        );
      } else if (typeof raw === "string" && raw.trim()) {
        out[key] = [formatTimeRangeStringToAmPm(raw.trim())];
      }
    }
    if (Object.keys(out).length > 0) return out;
  }

  const windows = payload.windows;
  if (!Array.isArray(windows) || windows.length === 0) return null;

  const byDay = {};
  for (const w of windows) {
    const dow = w.day_of_week ?? w.dayOfWeek;
    if (dow == null || dow < 0 || dow > 6) continue;
    const key = DAY_INDEX_TO_KEY[dow];
    if (!key) continue;
    const label = formatWindowSlot(w);
    if (!label) continue;
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(label);
  }
  return Object.keys(byDay).length > 0 ? byDay : null;
}

function parseHhMm24(timeStr) {
  if (timeStr == null || typeof timeStr !== "string") return null;
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min) || h > 23 || min > 59) return null;
  return { h, min };
}

function formatSingleTimeToAmPm(timeStr) {
  const p = parseHhMm24(timeStr);
  if (!p) return typeof timeStr === "string" ? timeStr : "";
  const period = p.h >= 12 ? "PM" : "AM";
  const hour12 = p.h % 12 || 12;
  return `${hour12}:${String(p.min).padStart(2, "0")} ${period}`;
}

function formatTimeRangeStringToAmPm(s) {
  if (s == null || typeof s !== "string") return s;
  if (s === "—") return s;
  if (/\b(am|pm)\b/i.test(s)) return s;
  return s.replace(/\b(\d{1,2}):(\d{2})(?::\d{2})?\b/g, (full) => {
    const p = parseHhMm24(full);
    if (!p) return full;
    return formatSingleTimeToAmPm(full);
  });
}

function formatWindowSlot(w) {
  const start = w.start_time ?? w.startTime ?? "";
  const end = w.end_time ?? w.endTime ?? "";
  if (start && end) {
    return `${formatSingleTimeToAmPm(start)} – ${formatSingleTimeToAmPm(end)}`;
  }
  if (start || end) {
    const a = start ? formatSingleTimeToAmPm(start) : "";
    const b = end ? formatSingleTimeToAmPm(end) : "";
    return `${a}${b}`;
  }
  return "";
}

function getSingleSlotTimeRange(weeklySlots) {
  if (!weeklySlots || typeof weeklySlots !== "object") return null;
  const activeDays = WEEKDAY_KEYS.filter(
    (key) => Array.isArray(weeklySlots[key]) && weeklySlots[key].length > 0
  );
  if (activeDays.length === 0) return null;
  const allSingleSlot = activeDays.every(
    (key) => weeklySlots[key].length === 1
  );
  if (!allSingleSlot) return null;
  const firstSlot = weeklySlots[activeDays[0]][0];
  const allSame = activeDays.every((key) => weeklySlots[key][0] === firstSlot);
  return allSame ? firstSlot : null;
}

function formatDaysWithAvailability(weeklySlots) {
  if (!weeklySlots || typeof weeklySlots !== "object") return null;
  const labels = [];
  for (const key of WEEKDAY_KEYS) {
    const slots = weeklySlots[key];
    if (Array.isArray(slots) && slots.length > 0) {
      labels.push(WEEKDAY_LABELS[key]);
    }
  }
  return labels.length > 0 ? labels.join(", ") : null;
}

function facilityToCard(f) {
  const id = f._id ?? f.id;
  let imgs =
    Array.isArray(f.facility_image_urls) && f.facility_image_urls.length > 0
      ? f.facility_image_urls
      : Array.isArray(f.image_urls) && f.image_urls.length > 0
        ? f.image_urls
        : Array.isArray(f.images) && f.images.length > 0
          ? f.images
          : [];
  if (imgs.length === 0 && f.facility_image_url) {
    imgs = [f.facility_image_url];
  }
  const cap = f.capacity;
  return {
    id,
    raw: f,
    title: f.name ?? f.title ?? "Untitled facility",
    description: f.description ?? "",
    thumbnail: f.thumbnail_url ?? "/img/new-icons/dumbbell.svg",
    days: f.days_label ?? f.days ?? "—",
    time: f.time_label ?? f.time ?? "—",
    duration: f.duration_minutes != null ? `${f.duration_minutes}m` : "—",
    location: f.location ?? "—",
    capacity: cap != null ? `${cap} Seats` : "—",
    galleryUrls: imgs.filter(Boolean),
    weeklySlots: f.weekly_slots ?? f.weeklySlots ?? null,
    status:
      f.status === "expired" || f.status === "inactive" ? "expired" : "active",
    hasHistory: Boolean(f.has_history ?? f.hasHistory),
  };
}

const SchedulingFacilities = ({ hasPressScheduling = false }) => {
  const { locals } = useRillContext();
  const activeUser = locals.activeUser;
  const theme = activeUser.webAppTheme || "dark";
  const canManageFacility = (f) =>
    canManageSchedulingRecord(f?.raw, activeUser);
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [editingFacility, setEditingFacility] = React.useState(null);
  const [
    facilityModalInitialScrollSection,
    setFacilityModalInitialScrollSection,
  ] = React.useState(null);
  const [blockTimeModalOpen, setBlockTimeModalOpen] = React.useState(false);
  const [blockTimePreset, setBlockTimePreset] = React.useState(null);
  const [activeFilter, setActiveFilter] = React.useState("active");
  const [menuOpenId, setMenuOpenId] = React.useState(null);
  const [facilities, setFacilities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [availabilityById, setAvailabilityById] = React.useState({});
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [filterQuery, setFilterQuery] = React.useState("");
  const [filterLocation, setFilterLocation] = React.useState("");
  const [filterCapMin, setFilterCapMin] = React.useState("");
  const [filterCapMax, setFilterCapMax] = React.useState("");
  const filterPanelRef = React.useRef(null);

  React.useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const fetchFacilities = React.useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const res = await fetch("/actions/scheduling/facilities", {
        method: "GET",
        credentials: "same-origin",
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) {
        setFacilities([]);
        return;
      }
      if (!res.ok) {
        const msg =
          data?.error ??
          data?.message ??
          (typeof data === "string" ? data : "Failed to load facilities");
        throw new Error(msg);
      }
      const list = Array.isArray(data) ? data : [];
      setFacilities(list.map(facilityToCard));
    } catch (e) {
      setLoadError(e?.message ?? "Failed to load facilities");
      setFacilities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  React.useEffect(() => {
    if (consumeSchedulingCreateIntent("facility")) {
      setEditingFacility(null);
      setFacilityModalInitialScrollSection(null);
      setCreateModalOpen(true);
    }
  }, []);

  const pendingEditIdRef = React.useRef(
    consumeSchedulingEditIntent("facility")
  );

  /** Prefetch availability so “Days” matches Classes-style detail row */
  React.useEffect(() => {
    if (loading || facilities.length === 0) return;
    let cancelled = false;
    (async () => {
      await Promise.all(
        facilities.map(async (s) => {
          if (!s?.id) return;
          try {
            const res = await fetch(
              `/actions/scheduling/facilities/${encodeURIComponent(s.id)}/availability-windows`,
              { method: "GET", credentials: "same-origin" }
            );
            const data = await res.json().catch(() => ({}));
            if (cancelled) return;
            if (!res.ok) return;
            const weeklySlots = normalizeAvailabilityToWeeklySlots(data);
            setAvailabilityById((prev) => ({
              ...prev,
              [s.id]: { weeklySlots },
            }));
          } catch {
            /* ignore */
          }
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, facilities]);

  const filtered = (() => {
    const base =
      activeFilter === "expired"
        ? facilities.filter((s) => s.status !== "active")
        : facilities.filter((s) => s.status === "active");
    const q = filterQuery.trim().toLowerCase();
    const loc = filterLocation.trim().toLowerCase();
    const minCap = filterCapMin ? Number(filterCapMin) : null;
    const maxCap = filterCapMax ? Number(filterCapMax) : null;
    return base.filter((s) => {
      if (q && !String(s.title || "").toLowerCase().includes(q)) return false;
      if (loc && !String(s.location || "").toLowerCase().includes(loc))
        return false;
      if (minCap != null || maxCap != null) {
        const cap = s.raw?.capacity != null ? Number(s.raw.capacity) : null;
        if (cap == null) return false;
        if (minCap != null && cap < minCap) return false;
        if (maxCap != null && cap > maxCap) return false;
      }
      return true;
    });
  })();

  const facilityFilterCount =
    (filterQuery.trim() ? 1 : 0) +
    (filterLocation.trim() ? 1 : 0) +
    (filterCapMin ? 1 : 0) +
    (filterCapMax ? 1 : 0);

  const openEditFacility = React.useCallback(
    async (cls, scrollSection = null) => {
      const base = cls.raw && typeof cls.raw === "object" ? { ...cls.raw } : {};
      let merged = base;
      if (!Array.isArray(merged.windows) || merged.windows.length === 0) {
        try {
          const res = await fetch(
            `/actions/scheduling/facilities/${encodeURIComponent(cls.id)}/availability-windows`,
            { method: "GET", credentials: "same-origin" }
          );
          const data = await res.json().catch(() => ({}));
          if (
            res.ok &&
            Array.isArray(data.windows) &&
            data.windows.length > 0
          ) {
            merged = {
              ...merged,
              windows: data.windows.map((w) => ({ ...w })),
            };
          }
        } catch {
          /* ignore */
        }
      }
      setFacilityModalInitialScrollSection(scrollSection);
      setEditingFacility(merged);
      setCreateModalOpen(true);
    },
    []
  );

  React.useEffect(() => {
    const id = pendingEditIdRef.current;
    if (!id || facilities.length === 0) return;
    const match = facilities.find(
      (f) => String(f.raw?._id ?? f.id) === String(id)
    );
    if (match) {
      pendingEditIdRef.current = null;
      if (!canManageSchedulingRecord(match.raw, activeUser)) return;
      openEditFacility(match);
    }
  }, [facilities, openEditFacility, activeUser]);

  const deleteFacility = React.useCallback(
    async (cls) => {
      const id = cls.raw?._id ?? cls.id;
      if (!id) return;
      if (!window.confirm("Delete this facility? This cannot be undone.")) {
        return;
      }
      try {
        const res = await fetch(
          `/actions/scheduling/facilities/${encodeURIComponent(String(id))}`,
          { method: "DELETE", credentials: "same-origin" }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data.error ?? data.message ?? "Failed to delete facility"
          );
        }
        setMenuOpenId(null);
        setAvailabilityById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        fetchFacilities();
      } catch (e) {
        window.alert(e?.message ?? "Failed to delete facility");
      }
    },
    [fetchFacilities]
  );

  const duplicateFacility = React.useCallback(
    async (cls) => {
      if (!cls?.raw) return;
      setMenuOpenId(null);
      try {
        const windows = await resolveFacilityWindowsForDuplicate(cls);
        if (windows.length === 0) {
          window.alert(
            "Add availability windows to this facility before duplicating."
          );
          return;
        }
        const payload = buildDuplicateFacilityPayload(cls.raw, windows);
        const res = await fetch("/actions/scheduling/facilities", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data.error ?? data.message ?? "Failed to duplicate facility"
          );
        }
        fetchFacilities();
      } catch (e) {
        window.alert(e?.message ?? "Failed to duplicate facility");
      }
    },
    [fetchFacilities]
  );

  return (
    <div className="scheduling-classes">
      <header className="scheduling-classes-header">
        <hgroup className="scheduling-classes-title-group">
          <h1 className="scheduling-classes-title">Facilities</h1>
          <p className="scheduling-classes-subtitle">
            Manage your facilities, set availability, and handle bookings with
            ease.
          </p>
          <div className="scheduling-classes-header-buttons">
            <button
              type="button"
              data-button-type="primary"
              className="scheduling-classes-create-btn"
              onClick={() => {
                setEditingFacility(null);
                setFacilityModalInitialScrollSection(null);
                setCreateModalOpen(true);
              }}
            >
              + Create Facilities
            </button>
            <button
              type="button"
              data-button-type="outline"
              className="scheduling-classes-block-time-btn"
              onClick={() => {
                setBlockTimePreset({
                  scheduleKind: BLOCK_TIME_SCHEDULE_FACILITIES,
                });
                setBlockTimeModalOpen(true);
              }}
            >
              <img
                src="/img/new-icons/slash.svg"
                alt=""
                className="scheduling-classes-block-icon"
              />
              Block Time : Facilities
              <span className="scheduling-classes-info-icon">
                <img
                  src="/img/new-icons/vuesax-linear-tick-circle-schedule.svg"
                  alt="Info"
                />
                <span
                  className="scheduling-classes-info-tooltip"
                  role="tooltip"
                >
                  Block time for all the Facilities. This will not block time
                  for classes and PT Sessions.
                </span>
              </span>
            </button>
          </div>
        </hgroup>
      </header>

      <div className="scheduling-classes-box">
        <nav className="scheduling-classes-tabs">
          <button
            type="button"
            className="scheduling-classes-tab"
            aria-current={activeFilter === "active" ? "page" : undefined}
            onClick={() => setActiveFilter("active")}
          >
            Active
          </button>
          <button
            type="button"
            className="scheduling-classes-tab"
            aria-current={activeFilter === "expired" ? "page" : undefined}
            onClick={() => setActiveFilter("expired")}
          >
            Expired
          </button>
          <div className="scheduling-classes-filter-wrap" ref={filterPanelRef}>
            <button
              type="button"
              className="scheduling-classes-filter-btn"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((o) => !o)}
            >
              <img src="/img/new-icons/filter-white.svg" alt="" />
              Filter
              {facilityFilterCount > 0 && (
                <span className="scheduling-classes-filter-badge">
                  {facilityFilterCount}
                </span>
              )}
            </button>
            {filterOpen && (
              <div className="scheduling-classes-filter-panel" role="dialog">
                <label className="scheduling-classes-filter-label">
                  Filter by name
                  <input
                    type="text"
                    className="scheduling-classes-filter-input"
                    placeholder="Search facilities…"
                    value={filterQuery}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="scheduling-classes-filter-label">
                  Location
                  <input
                    type="text"
                    className="scheduling-classes-filter-input"
                    placeholder="Search location…"
                    value={filterLocation}
                    onChange={(e) => setFilterLocation(e.target.value)}
                  />
                </label>
                <div className="scheduling-classes-filter-row">
                  <label className="scheduling-classes-filter-label">
                    Min capacity
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 5"
                      value={filterCapMin}
                      onChange={(e) => setFilterCapMin(e.target.value)}
                    />
                  </label>
                  <label className="scheduling-classes-filter-label">
                    Max capacity
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 50"
                      value={filterCapMax}
                      onChange={(e) => setFilterCapMax(e.target.value)}
                    />
                  </label>
                </div>
                <footer className="scheduling-classes-filter-footer">
                  <button
                    type="button"
                    data-button-type="tertiary"
                    onClick={() => {
                      setFilterQuery("");
                      setFilterLocation("");
                      setFilterCapMin("");
                      setFilterCapMax("");
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    data-button-type="primary"
                    onClick={() => setFilterOpen(false)}
                  >
                    Done
                  </button>
                </footer>
              </div>
            )}
          </div>
        </nav>

        {loading && <CardListSkeleton theme={theme} />}
        {!loading && loadError && (
          <p className="scheduling-classes-error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && facilities.length === 0 && (
          <SchedulingEmptyState
            iconSrc="/img/new-icons/facilities-icon.svg"
            title="No facilities yet"
            description="Create your first facility to begin reserving spaces."
            action={
              <button
                type="button"
                data-button-type="primary"
                onClick={() => setCreateModalOpen(true)}
              >
                + Create Facilities
              </button>
            }
          />
        )}
        <ul className="scheduling-classes-list">
          {filtered.map((cls) => {
            const daysDisplay =
              formatDaysWithAvailability(
                availabilityById[cls.id]?.weeklySlots
              ) ??
              formatDaysWithAvailability(cls.weeklySlots) ??
              cls.days;
            const gallery = cls.galleryUrls || [];
            const galleryShown = gallery.slice(0, 3);
            const galleryExtra = gallery.length > 3 ? gallery.length - 3 : null;
            const canManage = canManageFacility(cls);

            return (
              <li
                key={cls.id}
                className="scheduling-class-card scheduling-facility-card"
              >
                <div className="scheduling-class-card-checkbox">
                  <input
                    type="checkbox"
                    id={`facility-${cls.id}`}
                    aria-label={`Select ${cls.title}`}
                    disabled={!canManage}
                  />
                </div>
                <div className="scheduling-class-card-thumb">
                  <img src={cls.thumbnail} alt="" loading="lazy" />
                </div>
                <div className="scheduling-class-card-main">
                  <h3 className="scheduling-class-card-title">{cls.title}</h3>
                  <p className="scheduling-class-card-desc">
                    {cls.description}
                  </p>
                  <div className="scheduling-facility-card-info">
                    <div className="scheduling-facility-details-rows">
                      <div className="scheduling-facility-details-row">
                        <span title="Days">
                          <img src="/img/new-icons/calendar-ico.svg" alt="" />
                          {daysDisplay}
                        </span>
                        <span title="Duration">
                          <img
                            src="/img/new-icons/vuesax-linear-timer-pause.svg"
                            alt=""
                          />
                          {cls.duration}
                        </span>
                        <span title="Location">
                          <img src="/img/new-icons/map-pin.svg" alt="" />
                          {cls.location}
                        </span>
                      </div>
                      <div className="scheduling-facility-details-row">
                        <span title="Time">
                          <img
                            src="/img/new-icons/vuesax-linear-clock.svg"
                            alt=""
                          />
                          {getSingleSlotTimeRange(
                            availabilityById[cls.id]?.weeklySlots ??
                              cls.weeklySlots
                          ) ?? formatTimeRangeStringToAmPm(cls.time)}
                        </span>
                        <span title="Seats">
                          <img src="/img/new-icons/team-member.svg" alt="" />
                          {cls.capacity}
                        </span>
                      </div>
                    </div>
                    <div className="scheduling-facility-gallery">
                      {galleryShown.map((url, i) => (
                        <span
                          key={`${cls.id}-g-${i}`}
                          className="scheduling-facility-gallery-thumb"
                        >
                          <img src={url} alt="" loading="lazy" />
                        </span>
                      ))}
                      {galleryExtra != null && (
                        <span className="scheduling-facility-gallery-extra">
                          +{galleryExtra}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="scheduling-class-card-actions scheduling-facility-card-actions">
                  <div className="scheduling-facility-top-actions">
                    {canManage && (
                      <button
                        type="button"
                        className="scheduling-class-card-edit-btn"
                        onClick={() => openEditFacility(cls)}
                      >
                        <img src="/img/new-icons/edit-pencil.svg" alt="" />
                        Edit
                      </button>
                    )}
                    <div className="scheduling-class-card-menu-wrapper">
                      <button
                        type="button"
                        className="scheduling-class-card-menu-btn"
                        aria-expanded={menuOpenId === cls.id}
                        aria-haspopup="true"
                        onClick={() =>
                          setMenuOpenId(menuOpenId === cls.id ? null : cls.id)
                        }
                      >
                        <img
                          src="/img/new-icons/more-square.svg"
                          alt="More actions"
                        />
                      </button>
                      {menuOpenId === cls.id && (
                        <>
                          <div
                            className="scheduling-class-card-menu-backdrop"
                            onClick={() => setMenuOpenId(null)}
                            aria-hidden="true"
                          />
                          <ul className="scheduling-class-card-menu">
                            <li>
                              <a
                                href={buildSchedulingCalendarPreviewUrl(
                                  "facility",
                                  cls.id
                                )}
                                onClick={() => setMenuOpenId(null)}
                              >
                                <img
                                  src="/img/new-icons/vuesax-linear-play.svg"
                                  alt=""
                                />
                                Preview in Calendar
                              </a>
                            </li>
                            <li>
                              <button
                                type="button"
                                onClick={() => duplicateFacility(cls)}
                              >
                                <img
                                  src="/img/new-icons/duplicate.svg"
                                  alt=""
                                />
                                Duplicate
                              </button>
                            </li>
                            {canManage && (
                              <>
                                <li>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMenuOpenId(null);
                                      setBlockTimePreset({
                                        scheduleKind:
                                          BLOCK_TIME_SCHEDULE_FACILITIES,
                                        bookableId: String(cls.id),
                                        scrollSection:
                                          BLOCK_TIME_SCROLL_SELECT_DATE,
                                      });
                                      setBlockTimeModalOpen(true);
                                    }}
                                  >
                                    <img
                                      src="/img/new-icons/close-circle-outline-white.svg"
                                      alt=""
                                    />
                                    Block Time
                                  </button>
                                </li>
                                {(hasPressScheduling ||
                                  Boolean(cls.raw?.is_restricted)) && (
                                  <li>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setMenuOpenId(null);
                                        openEditFacility(
                                          cls,
                                          "access_permissions"
                                        );
                                      }}
                                    >
                                      <img
                                        src="/img/new-icons/lock.svg"
                                        alt=""
                                      />
                                      Permissions
                                    </button>
                                  </li>
                                )}
                                <li>
                                  <button
                                    type="button"
                                    className="scheduling-class-card-menu-delete"
                                    onClick={() => deleteFacility(cls)}
                                  >
                                    <img
                                      src="/img/new-icons/red-trash.svg"
                                      alt=""
                                    />
                                    Delete
                                  </button>
                                </li>
                              </>
                            )}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                  {cls.hasHistory && (
                    <button
                      type="button"
                      className="scheduling-facility-view-history"
                    >
                      <img src="/img/new-icons/history-icon.svg" alt="" />
                      View History
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <CreateBlockTimeModal
        key={
          blockTimeModalOpen && blockTimePreset
            ? `bt-${blockTimePreset.scheduleKind}-${blockTimePreset.bookableId}`
            : "block-time-closed-fac"
        }
        isOpen={blockTimeModalOpen}
        listsLoading={loading}
        classes={[]}
        ptSessions={[]}
        facilities={facilities
          .filter(canManageFacility)
          .map((f) => f.raw)
          .filter(Boolean)}
        initialScheduleKind={blockTimePreset?.scheduleKind ?? null}
        initialPreselectedBookableId={blockTimePreset?.bookableId ?? null}
        initialScrollSection={blockTimePreset?.scrollSection ?? null}
        onClose={() => {
          setBlockTimeModalOpen(false);
          setBlockTimePreset(null);
        }}
        onConfirm={async (payloads) => {
          await submitBlockTimePayloads(payloads);
          fetchFacilities();
        }}
      />

      <CreateFacilityModal
        key={
          createModalOpen
            ? String(editingFacility?._id ?? editingFacility?.id ?? "create")
            : "closed"
        }
        isOpen={createModalOpen}
        initialRecord={editingFacility}
        initialScrollSection={facilityModalInitialScrollSection}
        hasPressScheduling={hasPressScheduling}
        onClose={() => {
          setCreateModalOpen(false);
          setEditingFacility(null);
          setFacilityModalInitialScrollSection(null);
        }}
        onConfirm={async (payload, fileOptions = {}) => {
          const editId = editingFacility?._id ?? editingFacility?.id;
          const url = editId
            ? `/actions/scheduling/facilities/${encodeURIComponent(String(editId))}`
            : "/actions/scheduling/facilities";
          const method = editId ? "PUT" : "POST";
          const res = await fetchSchedulingBookable(
            url,
            method,
            payload,
            fileOptions.files ?? null
          );
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              data.error ||
                (editId
                  ? "Failed to update facility"
                  : "Failed to create facility")
            );
          }
          setEditingFacility(null);
          fetchFacilities();
        }}
      />
    </div>
  );
};

export default SchedulingFacilities;
